// Training-calendar utilities (year-keyed training starts)
//  - add years to TRAINING_START_BY_YEAR to support future training cycles
//  - months are zero-based (0=Jan, 7=Aug)
export const TRAINING_START_BY_YEAR: Record<number, { month: number; day: number }> = {
  2026: { month: 7, day: 3 }, // Monday, August 3, 2026
  // add future years here, e.g.:
  // 2027: { month: 7, day: 2 },
};

export const TOTAL_WEEKS = 8;

export interface WeekDates {
  startDate: Date; // Monday
  endDate: Date; // Friday
  startDateStr: string; // ISO date yyyy-mm-dd
  endDateStr: string;
  weekNumber: number;
  year: number;
}

/**
 * Return a Date at midnight local time for the provided year/month/day.
 */
const makeLocalDate = (year: number, month: number, day: number): Date => {
  const d = new Date(year, month, day);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getTrainingStartForYear = (year: number): Date | null => {
  const entry = TRAINING_START_BY_YEAR[year];
  if (!entry) return null;
  return makeLocalDate(year, entry.month, entry.day);
};

/**
 * If the provided date falls within any known training year's range
 * (start through start + TOTAL_WEEKS*7 - 1), returns that year.
 * Otherwise returns null.
 */
export const findTrainingYearForDate = (date: Date): number | null => {
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  const years = Object.keys(TRAINING_START_BY_YEAR)
    .map((s) => parseInt(s, 10))
    .sort((a, b) => a - b);

  for (const y of years) {
    const start = getTrainingStartForYear(y);
    if (!start) continue;
    const end = new Date(start);
    end.setDate(start.getDate() + TOTAL_WEEKS * 7 - 1); // last calendar day covered by training (includes weekends)
    if (checkDate >= start && checkDate <= end) return y;
  }

  return null;
};

/**
 * Get the start (Monday) and end (Friday) dates for a given week number (1-based) and year.
 * Returns WeekDates or throws if the year isn't configured or weekNumber out of range.
 */
export const getWeekDates = (weekNumber: number, year: number): WeekDates => {
  if (weekNumber < 1 || weekNumber > TOTAL_WEEKS) {
    throw new Error(`weekNumber must be between 1 and ${TOTAL_WEEKS}`);
  }

  // Hard-coded week dates for 2026
  const weekDateRanges: Record<number, { start: string; end: string }> = {
    1: { start: '2026-08-03', end: '2026-08-07' }, // Week 1: 03/08/2026 – 07/08/2026
    2: { start: '2026-08-10', end: '2026-08-14' }, // Week 2: 10/08/2026 – 14/08/2026
    3: { start: '2026-08-17', end: '2026-08-21' }, // Week 3: 17/08/2026 – 21/08/2026
    4: { start: '2026-08-24', end: '2026-08-28' }, // Week 4: 24/08/2026 – 28/08/2026
    5: { start: '2026-08-31', end: '2026-09-04' }, // Week 5: 31/08/2026 – 04/09/2026
    6: { start: '2026-09-07', end: '2026-09-11' }, // Week 6: 07/09/2026 – 11/09/2026
    7: { start: '2026-09-14', end: '2026-09-18' }, // Week 7: 14/09/2026 – 18/09/2026
    8: { start: '2026-09-21', end: '2026-09-25' }, // Week 8: 21/09/2026 – 25/09/2026
  };

  const dates = weekDateRanges[weekNumber];
  if (!dates) {
    throw new Error(`No dates configured for week ${weekNumber}`);
  }

  const weekStart = new Date(dates.start);
  const weekEnd = new Date(dates.end);

  return {
    startDate: weekStart,
    endDate: weekEnd,
    startDateStr: dates.start,
    endDateStr: dates.end,
    weekNumber,
    year,
  };
};

/**
 * For a given date, return the weekNumber (1..TOTAL_WEEKS) and WeekDates for the matching training year.
 * If the date is before the training start or after the configured training range, return { weekNumber: null }.
 */
export const getWeekForDate = (
  date: Date,
): { weekNumber: number | null; weekDates?: WeekDates; year?: number } => {
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  const year = findTrainingYearForDate(checkDate);
  if (!year) return { weekNumber: null };

  const trainingStart = getTrainingStartForYear(year)!;
  const daysDiff = Math.floor((Number(checkDate) - Number(trainingStart)) / (1000 * 3600 * 24));
  if (daysDiff < 0) return { weekNumber: null };

  const computedWeek = Math.floor(daysDiff / 7) + 1;
  if (computedWeek < 1 || computedWeek > TOTAL_WEEKS) return { weekNumber: null };

  const weekDates = getWeekDates(computedWeek, year);
  return { weekNumber: computedWeek, weekDates, year };
};

/**
 * Return the current week number for the "current" training (based on supplied date or now).
 * If the date is before the configured training start, returns null.
 * If after the training range, returns null (caller can treat as 'completed').
 */
export const getCurrentWeek = (now: Date = new Date()): number | null => {
  const result = getWeekForDate(now);
  return result.weekNumber;
};

export const isWeekInFuture = (weekNumber: number, year: number): boolean => {
  const now = new Date();
  const wd = getWeekDates(weekNumber, year);
  return now < wd.startDate;
};

export const isWeekCompleted = (weekNumber: number, year: number): boolean => {
  const now = new Date();
  const wd = getWeekDates(weekNumber, year);
  // after Friday means completed
  return now > wd.endDate;
};

export const isCurrentWeek = (weekNumber: number, year: number): boolean => {
  const now = new Date();
  const wd = getWeekDates(weekNumber, year);
  return now >= wd.startDate && now <= wd.endDate;
};
